import React, { useEffect } from "react";
import {
  TextInput,
  DateInput,
  NumberInput,
  FileInput,
  FileField,
  ArrayInput,
  SimpleFormIterator,
  SelectInput,
} from "react-admin";
import { useFormContext, useWatch } from "react-hook-form";
import { calculateRemainingFinance, MONTH_CHOICES } from "./vehicleFinance";

const buildYearChoices = () => {
  const current = new Date().getFullYear();
  const years = [];
  for (let y = current - 3; y <= current + 8; y += 1) {
    years.push({ id: y, name: String(y) });
  }
  return years;
};

export const VehicleFields = () => {
  const { setValue, getValues } = useFormContext();
  const yearChoices = buildYearChoices();

  // 🔹 Watch key finance fields
  const watchValues = useWatch({
    name: [
      "totalFacilityAmount",
      "monthlyInstallment",
      "installmentStart",
      "installmentEnd",
      "mortgageFrom",
      "deferredInstallments",
      "manualRevisedEndDate",
      "bankCharges",
      "settlementDate",
      "soldAt",
    ],
  });

  // 🔹 Auto-calculate Remaining Finance from shared finance helper
  useEffect(() => {
    if (!watchValues) return;

    const monthly = Number(watchValues[1]) || 0;
    const deferredInstallments = Array.isArray(watchValues[5])
      ? watchValues[5].map((item) => ({
          ...item,
          amount: Number(item?.amount) || monthly,
        }))
      : [];

    const remaining = calculateRemainingFinance({
      totalFacilityAmount: watchValues[0],
      monthlyInstallment: watchValues[1],
      installmentStart: watchValues[2],
      installmentEnd: watchValues[3],
      mortgageFrom: watchValues[4],
      deferredInstallments,
      manualRevisedEndDate: watchValues[6],
      bankCharges: watchValues[7],
      settlementDate: watchValues[8],
      soldAt: watchValues[9],
    });

    setValue("remainingFinance", remaining, {
      shouldDirty: false,
      shouldValidate: false,
    });
  }, [watchValues, setValue]);

  // 🔹 UI layout
  const row = {
    display: "flex",
    gap: "16px",
    alignItems: "flex-start",
    marginBottom: "12px",
    flexWrap: "wrap",
  };
  const col = { flex: 1, minWidth: "220px" };

  return (
    <div style={{ marginTop: 20 }}>
      <h4 style={{ marginBottom: 10 }}>Vehicle Information</h4>

      <div style={row}>
        <div style={col}>
          <TextInput source="vehicleNumber" label="Vehicle Number" fullWidth />
        </div>

        <div style={col}>
          <TextInput
            source="vehicleType"
            label="Vehicle Type / Model"
            helperText="Example: Toyota Highlander, Lexus RX350"
            fullWidth
          />
        </div>

        <div style={col}>
          <TextInput source="vehicleYear" label="Vehicle Year" fullWidth />
        </div>

        <div style={col}>
          <TextInput source="company" label="Company" fullWidth />
        </div>
      </div>

      {/* Insurance */}
      <h4 style={{ margin: "20px 0 8px" }}>Insurance Details</h4>
      <div style={row}>
        <div style={col}>
          <DateInput source="insuranceExpiry" label="Insurance Expiry" />
        </div>
        <div style={col}>
          <FileInput
            source="insuranceFile"
            label="Insurance Policy Document"
            accept="image/*,application/pdf"
          >
            <FileField source="src" title="title" />
          </FileInput>
        </div>
      </div>

      {/* Registration */}
      <h4 style={{ margin: "20px 0 8px" }}>Registration / Mulkiya</h4>
      <div style={row}>
        <div style={col}>
          <DateInput source="registrationExpiry" label="Registration Expiry" />
        </div>
        <div style={col}>
          <FileInput
            source="registrationFile"
            label="Registration Card / Mulkiya"
            accept="image/*,application/pdf"
          >
            <FileField source="src" title="title" />
          </FileInput>
        </div>
      </div>

      {/* Finance / Mortgage */}
      <h4 style={{ margin: "20px 0 8px" }}>Finance / Mortgage</h4>
      <div style={row}>
        <div style={col}>
          <TextInput
            source="mortgageFrom"
            label="Mortgage From (Dealer / Bank)"
            fullWidth
          />
        </div>
        <div style={col}>
          <TextInput
            source="installmentBank"
            label="Installment Deduction Bank Name"
            fullWidth
          />
        </div>
        <div style={col}>
          <NumberInput
            source="totalFacilityAmount"
            label="Total Facility Amount (AED)"
            fullWidth
          />
        </div>
      </div>

      <div style={row}>
        <div style={col}>
          <NumberInput
            source="monthlyInstallment"
            label="Monthly Installment (AED)"
            fullWidth
          />
        </div>
        <div style={col}>
          <DateInput
            source="installmentStart"
            label="Installment Start Date"
            fullWidth
          />
        </div>
        <div style={col}>
          <DateInput
            source="installmentEnd"
            label="Original Installment End Date"
            fullWidth
          />
        </div>
        <div style={col}>
          <NumberInput
            source="remainingFinance"
            label="Remaining Finance Amount (AED)"
            fullWidth
            disabled
          />
        </div>
      </div>

      {/* Finance Adjustment */}
      <h4 style={{ margin: "20px 0 8px" }}>Finance Adjustment / Deferred Installments</h4>
      <div style={row}>
        <div style={col}>
          <DateInput
            source="manualRevisedEndDate"
            label="Manual Revised End Date (optional)"
            fullWidth
          />
        </div>
        <div style={col}>
          <NumberInput
            source="bankCharges"
            label="Bank Charges / Interest (AED)"
            fullWidth
          />
        </div>
      </div>

      <ArrayInput source="deferredInstallments" label="Deferred Month & Year">
        <SimpleFormIterator inline>
          <SelectInput source="month" label="Month" choices={MONTH_CHOICES} />
          <SelectInput source="year" label="Year" choices={yearChoices} />
          <NumberInput
            source="amount"
            label="Amount (AED)"
            defaultValue={Number(getValues("monthlyInstallment")) || 0}
          />
          <TextInput source="remarks" label="Remarks" />
        </SimpleFormIterator>
      </ArrayInput>

      <div style={{ fontSize: 13, color: "#666", marginTop: -5, marginBottom: 12 }}>
        Add every deferred installment separately by month and year. If manual revised end date is blank, system extends the end date automatically by the number of deferred months.
      </div>

      {/* Final Settlement */}
      <h4 style={{ margin: "20px 0 8px" }}>Final Settlement</h4>
      <div style={row}>
        <div style={col}>
          <DateInput source="settlementDate" label="Settlement Date" fullWidth />
        </div>
        <div style={col}>
          <NumberInput source="settlementAmount" label="Settlement Amount (AED)" fullWidth />
        </div>
        <div style={col}>
          <TextInput source="settlementNotes" label="Settlement Notes / Reference" fullWidth />
        </div>
      </div>

      <div style={row}>
        <div style={col}>
          <FileInput
            source="financeFile"
            label="Finance / Mortgage Agreement"
            accept="image/*,application/pdf"
          >
            <FileField source="src" title="title" />
          </FileInput>
        </div>
      </div>
    </div>
  );
};
